/**
 * Release Run Pane Reveal
 *
 * @module features/releases/components/ReleaseRunPaneReveal
 * @description Reveals the timeline's detail pane out of the selected band.
 * The pane's column (`ReleasePropertyTimeline`) is always mounted at `xl`;
 * this component only clips a white surface *inside* that fixed column, so
 * the column (and the timeline beside it) never move because of it.
 *
 * The pane is physically connected to the selected band: it carries the
 * band's own `border-primary` + ring, the band's right border is cancelled
 * (`TimelineRunEntry`), and `ReleasePropertyTimeline` erases this surface's
 * left border across the band's rows, so the outline is one continuous
 * shape.
 *
 * The reveal is a single `clip-path: inset()` transition: the white starts as the band's
 * own slot at the junction (zero width, the band's rows) and expands to the
 * whole column in one sharp ease-out. The content stays transparent until
 * the surface is fully there, then fades in on its own; Close reverses it.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion, usePresence } from 'framer-motion';

/** A viewport rect — just the numbers this module needs. */
export type BandRect = { top: number; bottom: number };
/** A band's slice in column-local coordinates (px from the column's own top). */
export type BandSlice = { top: number; bottom: number };

const REVEAL_MS = 220;
const CONCEAL_MS = 160;
const CONTENT_FADE_IN_MS = 150;
const CONTENT_FADE_OUT_MS = 100;
const REDUCED_DURATION_MS = 150;
/** Sharp ease-out — most of the travel happens in the first third. */
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/** Column-relative band slice from viewport rects, clamped to the column's own bounds. `null` (no click origin) reveals from the column's top. */
export function relativeBandSlice(bandRect: BandRect | null, columnRect: BandRect | null): BandSlice {
  if (!bandRect || !columnRect) return { top: 0, bottom: 0 };
  const height = columnRect.bottom - columnRect.top;
  if (!(height > 0)) return { top: 0, bottom: 0 };
  const clamp = (v: number) => Math.min(Math.max(v, 0), height);
  return { top: clamp(bandRect.top - columnRect.top), bottom: clamp(bandRect.bottom - columnRect.top) };
}

/** The clip for the concealed state: the band's slot on the column's left edge. */
function slotClip(slice: BandSlice): string {
  return `inset(${slice.top}px 100% calc(100% - ${slice.bottom}px) 0)`;
}

/** Reveals `children` inside the pane's own surface. Mount this under an `AnimatePresence` keyed by run id so switching runs cross-fades. */
export function PaneReveal({ bandSlice, children }: { bandSlice: BandSlice; children: React.ReactNode }) {
  const reduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPresent, safeToRemove] = usePresence();
  const [revealed, setRevealed] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  // Reveal right after mount, then fade the content in once the surface is
  // fully there. The `offsetHeight` read forces the browser to compute the
  // slot clip first — without a computed start value the flip to `inset(0)`
  // would simply pop in instead of transitioning.
  useLayoutEffect(() => {
    if (!isPresent || reduceMotion) return;
    void containerRef.current?.offsetHeight;
    setRevealed(true);
    const content = setTimeout(() => setContentVisible(true), REVEAL_MS);
    return () => clearTimeout(content);
  }, [isPresent, reduceMotion]);

  // Exit: content fades out, the surface collapses back into the slot, then
  // AnimatePresence is told it's safe to unmount.
  useEffect(() => {
    if (isPresent) return;
    if (reduceMotion) {
      const t = setTimeout(safeToRemove, REDUCED_DURATION_MS);
      return () => clearTimeout(t);
    }
    setContentVisible(false);
    setRevealed(false);
    const t = setTimeout(safeToRemove, CONCEAL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresent]);

  if (reduceMotion) {
    return (
      <div
        className="flex flex-col flex-1 min-h-0 bg-card rounded-lg overflow-hidden border border-primary ring-1 ring-primary/40"
        style={{ opacity: isPresent ? 1 : 0, transition: `opacity ${REDUCED_DURATION_MS}ms ease-out` }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col flex-1 min-h-0 bg-card rounded-lg overflow-hidden border border-primary ring-1 ring-primary/40"
      style={{
        // `-2px` when revealed: `inset(0)` clips at the border box, which
        // would swallow the 1px ring drawn just outside it.
        clipPath: revealed ? 'inset(-2px)' : slotClip(bandSlice),
        transition: `clip-path ${revealed ? REVEAL_MS : CONCEAL_MS}ms ${EASE}`,
      }}
    >
      <div
        className="flex flex-col flex-1 min-h-0"
        style={{
          opacity: contentVisible ? 1 : 0,
          pointerEvents: contentVisible ? 'auto' : 'none',
          transition: `opacity ${contentVisible ? CONTENT_FADE_IN_MS : CONTENT_FADE_OUT_MS}ms ease-out`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
